import React, { forwardRef, useCallback, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Container, Modal, Nav, Navbar } from 'react-bootstrap';
import {
  ArrowUp,
  Bag,
  CalendarHeart,
  ChatDots,
  ClockHistory,
  List,
  Paperclip,
  Person,
  XLg,
} from 'react-bootstrap-icons';
import { NavLink, useLocation } from 'react-router-dom';
import { useTheme } from '../ThemeContext';

const buildStyles = (theme) => ({
  navbar: {
    minHeight: '56px',
    height: 'auto',
    left: '10px',
    right: '10px',
    bottom: '10px',
    borderRadius: '28px',
    boxShadow: theme === 'dark'
      ? '0 16px 34px rgba(0,0,0,0.38)'
      : '0 14px 30px rgba(28, 54, 84, 0.2)',
    backdropFilter: 'blur(16px) saturate(1.12)',
    WebkitBackdropFilter: 'blur(16px) saturate(1.12)',
    border: `1px solid ${theme === 'dark' ? 'rgba(202, 222, 241, 0.2)' : 'rgba(255, 255, 255, 0.62)'}`,
    background: theme === 'dark' ? 'rgba(34, 49, 64, 0.64)' : 'rgba(255, 255, 255, 0.62)',
    padding: '6px 6px 8px',
    alignItems: 'stretch',
  },
});

const ChatBottomBar = forwardRef(({
  value,
  onChange,
  onSubmit,
  disabled,
  replyName,
  replyText,
  onCancelReply,
  isEditing,
  editText,
  onCancelEdit,
  onPickPhoto,
  photoPreviewUrl,
  photoName,
  onRemovePhoto,
  isPhotoUploading,
  disablePhotoPick,
  disablePhotoRemove,
  isPromoMode,
  promoHintText,
  onSubmitPromo,
  onDismissPromoMode,
  isPromoLoading,
  placeholder,
  isSending,
}, ref) => {
  const { theme } = useTheme();
  const location = useLocation();
  const isDark = theme === 'dark';
  const styles = buildStyles(theme);
  const hasReply = Boolean(replyName || replyText);
  const hasEdit = Boolean(isEditing);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [isManualInputFocus, setIsManualInputFocus] = useState(false);
  const navbarRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const directInputFocusIntentRef = useRef(false);

  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';

    const computed = window.getComputedStyle(textarea);
    const lineHeight = Number.parseFloat(computed.lineHeight) || 20;
    const paddingTop = Number.parseFloat(computed.paddingTop) || 0;
    const paddingBottom = Number.parseFloat(computed.paddingBottom) || 0;
    const minHeight = 42;
    const maxHeight = Math.ceil((lineHeight * 10) + paddingTop + paddingBottom);

    if (!String(textarea.value || '').trim()) {
      textarea.style.height = `${minHeight}px`;
      textarea.style.overflowY = 'hidden';
      return;
    }

    const nextHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);

    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  useLayoutEffect(() => {
    resizeTextarea();
  }, [resizeTextarea, value, hasEdit, hasReply]);

  useImperativeHandle(ref, () => ({
    getElement: () => navbarRef.current,
    focusInput: () => {
      const textarea = textareaRef.current;
      if (!textarea || typeof textarea.focus !== 'function') return;
      textarea.focus();
      const nextPosition = String(textarea.value || '').length;
      if (typeof textarea.setSelectionRange === 'function') {
        textarea.setSelectionRange(nextPosition, nextPosition);
      }
    },
  }), []);

  const menuItems = useMemo(() => {
    const pathname = location.pathname;
    const items = [];

    if (pathname !== '/') items.push({ path: '/', icon: <List size={20} />, label: 'Главная' });
    if (pathname !== '/waiting-list') items.push({ path: '/waiting-list', icon: <ClockHistory size={20} />, label: 'Лист ожидания' });
    if (pathname !== '/chat') items.push({ path: '/chat', icon: <ChatDots size={20} />, label: 'Чат' });
    if (pathname !== '/market') items.push({ path: '/market', icon: <Bag size={20} />, label: 'Барахолка' });
    if (pathname !== '/my-market-items') items.push({ path: '/my-market-items', icon: <Bag size={20} />, label: 'Мои товары' });

    items.push(
      { path: '/yesterday-sales', icon: <CalendarHeart size={20} />, label: 'Вчерашние скидки' },
      { path: '/profile', icon: <Person size={20} />, label: 'Профиль' },
    );

    return items;
  }, [location.pathname]);

  const menuModalStyles = {
    item: {
      padding: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      color: theme === 'dark' ? '#e6eff9' : '#2f4154',
      transition: 'background 0.2s',
      cursor: 'pointer',
      borderRadius: '14px',
    },
  };

  const hasPhoto = Boolean(photoPreviewUrl);
  const handleSubmit = useCallback((event) => {
    event.preventDefault();
    if (isPromoMode) {
      if (!isPromoLoading && typeof onSubmitPromo === 'function') {
        onSubmitPromo();
      }
      return;
    }
    if (!disabled && typeof onSubmit === 'function') {
      onSubmit();
    }
  }, [disabled, isPromoLoading, isPromoMode, onSubmit, onSubmitPromo]);

  return (
    <>
      <Navbar
        ref={navbarRef}
        fixed="bottom"
        style={styles.navbar}
        className={`navbar app-bottom-nav chat-bottom-bar ${isDark ? 'app-bottom-nav--dark chat-bottom-bar--dark' : 'app-bottom-nav--light chat-bottom-bar--light'}`}
      >
        <Container fluid className="chat-bottom-bar__container">
          {(hasEdit || hasReply) && (
            <div className={`chat-reply-strip ${hasEdit ? 'chat-edit-strip' : ''} chat-bottom-bar__reply ${isDark ? 'chat-reply-strip--dark' : 'chat-reply-strip--light'}`}>
              <div className="chat-reply-strip-main">
                <div className="chat-reply-strip-name">{hasEdit ? 'Редактирование' : replyName}</div>
                <div className="chat-reply-strip-text">{hasEdit ? editText : replyText}</div>
              </div>
              <button
                type="button"
                className="chat-reply-strip-cancel"
                onClick={hasEdit ? onCancelEdit : onCancelReply}
              >
                Отмена
              </button>
            </div>
          )}

          {hasPhoto && (
            <div className={`chat-reply-strip chat-photo-strip chat-bottom-bar__reply ${isDark ? 'chat-reply-strip--dark' : 'chat-reply-strip--light'}`}>
              <div className="chat-photo-strip-preview-wrap">
                <img
                  src={photoPreviewUrl}
                  alt={photoName || 'Выбранное фото'}
                  className="chat-photo-strip-preview"
                />
              </div>
              <div className="chat-reply-strip-main">
                <div className="chat-reply-strip-name">
                  {isPhotoUploading ? 'Загружаю фото…' : 'Фото готово к отправке'}
                </div>
                <div className="chat-reply-strip-text">
                  {photoName || 'Изображение'}
                </div>
              </div>
              <button
                type="button"
                className="chat-reply-strip-cancel"
                onClick={onRemovePhoto}
                disabled={disablePhotoRemove}
              >
                Убрать
              </button>
            </div>
          )}

          {isPromoMode && (
            <div className={`chat-reply-strip chat-promo-strip chat-bottom-bar__reply ${isDark ? 'chat-reply-strip--dark' : 'chat-reply-strip--light'}`}>
              <div className="chat-reply-strip-main">
                <div className="chat-reply-strip-name">Подбор промокодов</div>
                <div className="chat-reply-strip-text">{promoHintText || 'Запрос не уйдет в чат'}</div>
              </div>
              <button
                type="button"
                className="chat-promo-strip-action"
                onClick={onSubmitPromo}
                disabled={isPromoLoading}
              >
                {isPromoLoading ? 'Ищу...' : 'Подобрать'}
              </button>
              <button
                type="button"
                className="chat-promo-strip-cancel"
                onClick={onDismissPromoMode}
                aria-label="Отключить подбор промокодов"
              >
                <XLg size={14} />
              </button>
            </div>
          )}

          <form
            className={`chat-composer-form chat-bottom-bar__form ${isManualInputFocus ? 'chat-bottom-bar__form--input-expanded' : ''} ${isPromoMode ? 'chat-bottom-bar__form--promo' : ''}`}
            onSubmit={handleSubmit}
          >
            <button
              type="button"
              className={`chat-composer-menu-btn chat-composer-main-menu-btn app-bottom-nav__item ${isDark ? 'app-bottom-nav__item--dark' : 'app-bottom-nav__item--light'}`}
              aria-label="Меню"
              onClick={() => setShowMenuModal(true)}
            >
              <List size={20} />
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="chat-composer-file-input"
              onChange={(event) => {
                const file = event.target.files?.[0] || null;
                if (file && typeof onPickPhoto === 'function') {
                  onPickPhoto(file);
                }
                event.target.value = '';
              }}
            />

            {!isPromoMode && (
              <button
                type="button"
                className={`chat-composer-menu-btn chat-composer-attach-btn app-bottom-nav__item ${isDark ? 'app-bottom-nav__item--dark' : 'app-bottom-nav__item--light'}`}
                aria-label="Прикрепить фото"
                disabled={disablePhotoPick}
                onClick={() => {
                  if (disablePhotoPick) return;
                  fileInputRef.current?.click();
                }}
              >
                <Paperclip size={18} />
              </button>
            )}

            <div className="chat-composer-input-wrap">
              <textarea
                ref={textareaRef}
                rows={1}
                maxLength={2000}
                placeholder={placeholder || (hasEdit ? 'Редактировать сообщение' : 'Введите сообщение')}
                value={value}
                className="chat-composer-input"
                onPointerDown={() => {
                  directInputFocusIntentRef.current = true;
                }}
                onChange={(event) => {
                  onChange(event.target.value);
                  window.requestAnimationFrame(resizeTextarea);
                }}
                onFocus={() => {
                  if (directInputFocusIntentRef.current) {
                    setIsManualInputFocus(true);
                  }
                  directInputFocusIntentRef.current = false;
                }}
                onBlur={() => {
                  directInputFocusIntentRef.current = false;
                  setIsManualInputFocus(false);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    if (isPromoMode) {
                      if (!isPromoLoading && typeof onSubmitPromo === 'function') {
                        onSubmitPromo();
                      }
                    } else if (!disabled && typeof onSubmit === 'function') {
                      onSubmit();
                    }
                  }
                }}
              />
            </div>

            {!isPromoMode && (
              <button
                type="submit"
                className={`chat-composer-send-btn ${isSending ? 'chat-composer-send-btn--loading' : ''}`}
                disabled={disabled}
                aria-label={isSending ? 'Sending...' : 'Send'}
              >
                <span className="chat-composer-send-icon" aria-hidden="true">
                  <ArrowUp size={20} />
                </span>
                <span className="chat-composer-send-loader" aria-hidden="true">
                  <span className="chat-composer-send-loader-rail" />
                  <span className="chat-composer-send-loader-runner" />
                </span>
              </button>
            )}
          </form>
        </Container>
      </Navbar>

      <Modal
        show={showMenuModal}
        onHide={() => setShowMenuModal(false)}
        animation
        backdropClassName="menu-backdrop"
        dialogClassName="menu-modal"
        centered={false}
        transition={null}
        enforceFocus={false}
      >
        <Modal.Body
          className={`app-menu-modal-body ${theme === 'dark' ? 'app-menu-modal-body--dark' : 'app-menu-modal-body--light'}`}
          style={{
            padding: '0',
            backgroundColor: 'transparent',
            borderRadius: '15px',
          }}
        >
          {menuItems.map((item) => (
            <Nav.Link
              key={item.path}
              as={NavLink}
              to={item.path}
              style={menuModalStyles.item}
              className={`app-menu-modal-item ${theme === 'dark' ? 'app-menu-modal-item--dark' : 'app-menu-modal-item--light'}`}
              onClick={() => setShowMenuModal(false)}
            >
              {item.icon}
              {item.label}
            </Nav.Link>
          ))}
        </Modal.Body>
      </Modal>
    </>
  );
});

ChatBottomBar.displayName = 'ChatBottomBar';

export default ChatBottomBar;
